import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { photoTypeLabel } from '../utils/photoCapture';
import { resolvePhotoUrl } from '../utils/photoUrl';
import PhotoViewer, { PhotoCaptureLine } from './PhotoViewer';
import { collapseBundles } from '../utils/zstack/bundle';
import FeatureOnHomeButton from './home/FeatureOnHomeButton';
import PhotoAnnotator from './annotation/PhotoAnnotator';

// The capture line is written in PhotoViewer; the map's drawer (PointDrawer)
// still imports it from here.
export { PhotoCaptureLine };

/**
 * Thumbnails that open the photograph at full resolution, magnifiable.
 *
 * One component for every place photos are shown -- a record's detail modal
 * (PhotoSection), the Bank dashboard, the national map's list of public
 * sightings with no location, the AI panel's reference photos, the photo
 * library -- where each used to hand-roll
 * its own <img> grid, two of them with no way to see the picture larger than a
 * postage stamp.
 *
 *   - URLs go through resolvePhotoUrl, so a desktop photo loads on a phone or
 *     through the LAN as well as on the laptop that stored it.
 *   - Thumbnails are the photo's small copy where the API names one
 *     (`thumbnail_url`: Field Mode stores a ~240 px copy beside each photo, and
 *     the server answers the original where there is none), else the original
 *     drawn small. A copy that does not load falls back to the original once.
 *     Either way they are lazy-loaded, so a long gallery does not fetch every
 *     image up front.
 *   - A photo is labelled by its view in the reader's language
 *     (common:photoType, the one catalogue the map's drawer uses too).
 *   - The viewer (PhotoViewer, shared with the hub's lightbox) shows the
 *     original at the largest size the screen allows, with previous/next,
 *     arrow keys, Escape, a link to the file itself, and zoom (ZoomableImage:
 *     buttons, wheel, double-click, pinch, +/-/0). Under it, when and where
 *     the photo was taken and how sure that fix was, where the photo carries
 *     those tags (utils/photoCapture).
 *   - A LARGE set -- more than `collapseAfter` photographs, as an import can
 *     attach -- shows its first thumbnails and a folder tile. The folder opens
 *     one window over the whole set: filterable, paged, and every photograph in
 *     it reachable in the viewer, so a record with 300 photos does not render
 *     300 thumbnails inline.
 *   - A photo that fails to load says so instead of showing a broken image.
 *   - A Z-stack (0095: the lists' bundle_id and bundle_cover) shows as its
 *     cover alone, its layers stepped through in the viewer
 *     (zstack/BundleLayers.js), so twelve layers are not twelve tiles.
 *
 * photos: [{ s3_url, photo_id?, photo_type?, notes?, taxon_name?, record_code?,
 *            source? ('reference' marks the imported reference set),
 *            thumbnail_url?, captured_at?, capture_lat?, capture_lng?,
 *            capture_accuracy_m? }]
 */
export const DEFAULT_COLLAPSE_AFTER = 24;
export const FOLDER_PAGE_SIZE = 60;

export default function PhotoGallery({
  photos = [], thumbWidth = 110, thumbHeight = 80, onDelete, deleteTitle, testId = 'photo-gallery',
  collapseAfter = DEFAULT_COLLAPSE_AFTER, folderTitle, annotatable = false,
}) {
  const { t } = useTranslation('common');
  const items = useMemo(() => collapseBundles(photos.filter(p => p && p.s3_url)), [photos]);
  const [index, setIndex] = useState(null);
  const [broken, setBroken] = useState({});
  const [fellBack, setFellBack] = useState({});   // small copy failed: show the original
  const [folderOpen, setFolderOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [annotating, setAnnotating] = useState(null);

  const count = items.length;
  const open = index !== null && index < count ? items[index] : null;
  const label = useCallback(p => photoTypeLabel(t, p.photo_type), [t]);
  const close = useCallback(() => setIndex(null), []);

  const collapsed = count > collapseAfter;
  const inline = collapsed ? items.slice(0, collapseAfter) : items;

  const matching = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const indexed = items.map((p, i) => ({ p, i }));
    if (!q) return indexed;
    return indexed.filter(({ p }) => [label(p), p.taxon_name, p.record_code, p.notes]
      .some(v => (v || '').toLowerCase().includes(q)));
  }, [items, filter, label]);
  const pages = Math.max(1, Math.ceil(matching.length / FOLDER_PAGE_SIZE));
  const currentPage = Math.min(page, pages - 1);
  const pageItems = matching.slice(currentPage * FOLDER_PAGE_SIZE, (currentPage + 1) * FOLDER_PAGE_SIZE);

  // The viewer handles its own keys (PhotoViewer); Escape reaches the folder
  // only while no photograph is open over it.
  useEffect(() => {
    if (!folderOpen || open) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') setFolderOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, folderOpen]);

  if (count === 0) return null;

  const keyOf = (p, i) => p.photo_id || `${p.s3_url}-${i}`;

  const thumb = (p, i, withDelete) => {
    const key = keyOf(p, i);
    const small = p.thumbnail_url && p.thumbnail_url !== p.s3_url && !fellBack[key] ? p.thumbnail_url : null;
    const failed = () => (small ? setFellBack(f => ({ ...f, [key]: true })) : setBroken(b => ({ ...b, [key]: true })));
    return (
      <figure key={key} style={{ margin: 0, position: 'relative', width: thumbWidth }}>
        <button type="button" onClick={() => setIndex(i)}
                aria-label={t('photoViewer.open', { type: label(p) })} title={t('tip.photoOpen')}
                style={{ ...thumbButton, width: thumbWidth, height: thumbHeight }}>
          {broken[key]
            ? <span style={brokenStyle}>{t('photoViewer.unavailable')}</span>
            : <img src={resolvePhotoUrl(small || p.s3_url)} alt={label(p)} loading="lazy" decoding="async"
                   onError={failed}
                   style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        </button>
        <figcaption style={captionStyle}>
          {label(p)}
          {p.source === 'reference' && <span style={referenceTag}> · {t('photoViewer.reference')}</span>}
        </figcaption>
        {withDelete && onDelete && p.photo_id && (
          <button type="button" onClick={() => onDelete(p)} title={deleteTitle} aria-label={deleteTitle}
                  style={deleteButton}>×</button>
        )}
      </figure>
    );
  };

  const openFolder = () => { setFilter(''); setPage(0); setFolderOpen(true); };

  return (
    <>
      <div data-testid={testId} style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {inline.map((p, i) => thumb(p, i, true))}
        {collapsed && (
          <button type="button" data-testid="photo-folder-tile" onClick={openFolder}
                  aria-label={t('photoViewer.openFolder', { count })} title={t('tip.photoFolder')}
                  style={{ ...thumbButton, ...folderTile, width: thumbWidth, height: thumbHeight }}>
            <span style={{ fontSize: 22 }}>🗂</span>
            <span>{t('photoViewer.moreInFolder', { count: count - collapseAfter })}</span>
          </button>
        )}
      </div>

      {folderOpen && (
        <div data-testid="photo-folder" style={overlayStyle} onClick={() => setFolderOpen(false)}>
          <div role="dialog" aria-modal="true" aria-label={folderTitle || t('photoViewer.folderTitle', { count })}
               style={folderStyle} onClick={e => e.stopPropagation()}>
            <div style={folderHeader}>
              <strong>{folderTitle || t('photoViewer.folderTitle', { count })}</strong>
              <input type="search" value={filter} onChange={e => { setFilter(e.target.value); setPage(0); }}
                     placeholder={t('photoViewer.folderFilter')} aria-label={t('photoViewer.folderFilter')}
                     title={t('tip.photoFilter')} style={filterInput} />
              <button type="button" onClick={() => setFolderOpen(false)} aria-label={t('photoViewer.closeFolder')}
                      title={t('tip.photoCloseFolder')} style={{ ...closeButton, position: 'static' }}>×</button>
            </div>
            <div style={folderGrid}>
              {pageItems.length === 0
                ? <p style={{ color: 'var(--c-text-muted-fg)', fontSize: 13 }}>{t('photoViewer.noMatch')}</p>
                : pageItems.map(({ p, i }) => thumb(p, i, false))}
            </div>
            {pages > 1 && (
              <div style={folderFooter}>
                <button type="button" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 0}
                        title={t('tip.photoPrevPage')} style={pageButton}>{t('photoViewer.previousPage')}</button>
                <span style={{ fontSize: 13 }}>{t('photoViewer.page', { page: currentPage + 1, pages })}</span>
                <button type="button" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= pages - 1}
                        title={t('tip.photoNextPage')} style={pageButton}>{t('photoViewer.nextPage')}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {annotating && <PhotoAnnotator photo={annotating} onClose={() => setAnnotating(null)} />}

      {open && (
        <PhotoViewer photos={items} index={index} onIndex={setIndex} onClose={close}
                     annotatable={annotatable} onAnnotate={setAnnotating}
                     extraActions={(photo) => <FeatureOnHomeButton photo={photo} />} />
      )}
    </>
  );
}

const thumbButton = {
  padding: 0, border: '1px solid var(--c-border)', borderRadius: 6, overflow: 'hidden', cursor: 'zoom-in',
  background: 'var(--c-bg-muted)', display: 'block',
};
const folderTile = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
  cursor: 'pointer', fontSize: 12, color: 'var(--c-primary-fg)', background: 'var(--c-primary-soft-2)', borderColor: 'var(--c-primary-border)',
};
const brokenStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%',
  fontSize: 11, color: 'var(--c-text-faint-fg)', textAlign: 'center', padding: 4, boxSizing: 'border-box',
};
const captionStyle = { fontSize: 11, color: 'var(--c-text-muted-fg)', textAlign: 'center', marginTop: 2, lineHeight: 1.3 };
const referenceTag = { color: 'var(--c-purple-fg)' };
const deleteButton = {
  position: 'absolute', top: 2, insetInlineEnd: 2, background: 'rgba(0,0,0,0.55)', color: 'var(--c-on-accent)', border: 'none',
  borderRadius: 4, width: 20, height: 20, cursor: 'pointer', fontSize: 13, lineHeight: '18px', padding: 0,
};
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: 12,
};
const folderStyle = {
  background: 'var(--c-surface)', borderRadius: 10, width: 'min(1100px, 96vw)', maxHeight: '92vh', display: 'flex',
  flexDirection: 'column', overflow: 'hidden',
};
const folderHeader = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--c-border)',
};
const filterInput = { flex: 1, padding: '6px 10px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 14 };
const folderGrid = { display: 'flex', flexWrap: 'wrap', gap: 10, padding: 14, overflowY: 'auto' };
const folderFooter = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '8px 14px',
  borderTop: '1px solid var(--c-border)',
};
const pageButton = {
  background: 'var(--c-bg-muted)', border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13,
};
const closeButton = {
  position: 'absolute', top: 8, insetInlineEnd: 8, background: 'rgba(0,0,0,0.55)', color: 'var(--c-on-accent)', border: 'none',
  borderRadius: '50%', width: 30, height: 30, fontSize: 20, cursor: 'pointer', lineHeight: '28px', padding: 0,
};
